import { TreatmentBundle } from '@customTypes/TreatmentBundle'
import { publicClient } from './apiClients'
import { TreatmentCancellable } from '@customTypes/TreatmentCancellable'
import {
  CategorizedTreatment,
  CategorizedTreatmentsWithPartialTreatments,
  AssortedCategorizedTreatment,
  CategorizedAndUniqueTreatments,
} from '@customTypes/CategorizedTreatment'
import { TreatmentUpsellDetails } from '@customTypes/TreatmentUpsellDetails'
import { InsuranceTypeOptions } from 'src/config/insuranceTypeOptions'
import { cloneDeep } from 'lodash'
import { suggestedTreamentsPurposeIds } from 'src/config/constants'
import { isProduction } from 'src/config'

export const getTreatment = async (clinicReferenceId: string, treatmentBundleId: string): Promise<TreatmentBundle> => {
  const { data } = await publicClient.get<TreatmentBundle>(`/bookable-treatment-bundles/${treatmentBundleId}`, {
    params: {
      clinicReferenceId,
    },
  })

  return data
}

export const isTreatmentCancellable = async (appointmentBundleReferenceId: string): Promise<boolean> => {
  const { data } = await publicClient.get<TreatmentCancellable>(
    `/appointment-bundles/${appointmentBundleReferenceId}/cancellable`
  )

  return data.isCancellable
}

export const getCategorizedTreatments = async (clinicReferenceId: string): Promise<CategorizedTreatment[]> => {
  const { data } = await publicClient.get('bookable-treatment-bundles/categorised', {
    params: {
      clinicReferenceId,
    },
  })

  return data
}

const getCategorizedAndUniqueTreatments = (data: AssortedCategorizedTreatment[]): CategorizedAndUniqueTreatments => {
  const categorizedTreatments: CategorizedTreatmentsWithPartialTreatments[] = data.map((category) => ({
    iconUrl: category.iconUrl,
    titleKey: category.contentfulKeyCategoryName,
    treatmentBundles: category.treatments.map((treatment) => ({
      referenceId: treatment.referenceId,
      clinicReferenceId: treatment.clinicReferenceId,
      titleKey: treatment.contentfulKey,
      descriptionKey: treatment.contentfulKeyDescription,
      treatmentPurposeReferenceId: treatment.treatmentPurposeReferenceId,
    })),
  }))

  const uniqueCategorizedTreatments = cloneDeep(categorizedTreatments)
  const uniqueKeys: Set<string> = new Set()

  uniqueCategorizedTreatments.forEach((category) => {
    category.treatmentBundles = category.treatmentBundles.filter((treatment) => {
      if (!treatment.treatmentPurposeReferenceId) return false

      if (uniqueKeys.has(treatment.titleKey || '')) {
        return false
      } else {
        uniqueKeys.add(treatment.titleKey || '')
        return true
      }
    })
  })

  return { uniqueCategorizedTreatments, categorizedTreatments }
}

export const getSuggestedTreatmentsCategory = (
  categorizedTreatments?: CategorizedTreatmentsWithPartialTreatments[]
) => {
  const flatTreatments = categorizedTreatments?.flatMap((category) => category.treatmentBundles) || []
  const treatmentPurposesIds = isProduction()
    ? suggestedTreamentsPurposeIds.production
    : suggestedTreamentsPurposeIds.staging
  const suggestedTreatments = treatmentPurposesIds
    .map((id) => flatTreatments.find((t) => t.treatmentPurposeReferenceId === id))
    .filter((t): t is Partial<TreatmentBundle> => !!t)

  const suggestedTreatmentsCategory: CategorizedTreatmentsWithPartialTreatments = {
    iconUrl: '',
    titleKey: 'suggested_treatments_category',
    treatmentBundles: suggestedTreatments,
    visibleIndex: 0,
  }

  return suggestedTreatmentsCategory
}

export const getAllCategorizedTreatments = async (city?: string): Promise<CategorizedAndUniqueTreatments> => {
  const params = {
    city,
    company: 'D21',
  }

  const { data } = await publicClient.get<AssortedCategorizedTreatment[]>('bookable-treatment-bundles/assorted', {
    params,
  })

  return getCategorizedAndUniqueTreatments(data)
}

export const getTreatmentUpsellDetails = async (
  clinicReferenceId: string,
  treatmentBundleTypeReferenceId: string,
  insuranceType: InsuranceTypeOptions
): Promise<TreatmentUpsellDetails | undefined> => {
  const { data } = await publicClient.get<TreatmentUpsellDetails[]>(
    `bookable-treatment-bundles/${treatmentBundleTypeReferenceId}/upsells`,
    {
      params: {
        clinicReferenceId,
        insuranceType,
      },
    }
  )

  return data?.[0]
}
